import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import { UploadFile } from "@mui/icons-material";

import { UploadProduct } from "types/products";
import { ENROLL_PATH, ONBOARDING_STEPS } from "types/onboarding";
import { GlobalConfigType } from "types/configs";

import { updateUserConfig } from "models/configs";
import { useSelfServeAlert } from "pages/SelfServe/utils/useSelfServeAlert";

import { useAuth } from "providers/Auth";
import { useGlobalConfig } from "providers/GlobalConfig";

import Stepper from "pages/SelfServe/sharedComponents/Stepper";
import ProductUploadModal from "pages/SelfServe/sharedComponents/ProductUploadModal";
import ProductPreview from "pages/SelfServe/sharedComponents/ProductPreview";

import { ManualUpload } from "./ManualUpload";
import { SaveAndContinueButton } from "pages/SelfServe/sharedComponents/SaveAndContinueButton";

import { sanitizeName, extractASIN } from "../../utils/catalogUpload";

type ManualErr = Partial<Record<keyof UploadProduct, string>>;

const ProductCatalogUpload: React.FC = () => {
  const [products, setProducts] = useState<UploadProduct[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [manual, setManual] = useState<UploadProduct>({
    name: "",
    sku: "",
    amazonId: "",
    upc: "",
    brand: "",
    map: "",
  });
  const [manualErr, setManualErr] = useState<ManualErr>({});

  const { sendAlert } = useSelfServeAlert();
  const navigate = useNavigate();
  const auth = useAuth();
  const { globalConfig } = useGlobalConfig()! as GlobalConfigType;

  const canContinue = useMemo(() => products.length > 0, [products]);

  const knownAsins = useMemo(
    () =>
      new Set(products.map((p) => p.amazonId?.toUpperCase()).filter(Boolean)),
    [products]
  );

  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target as {
      name: keyof UploadProduct;
      value: string;
    };
    setManual((prev) => ({ ...prev, [name]: value }));
    setManualErr((prev) => ({ ...prev, [name]: "" }));
  };

  const validateManual = (): boolean => {
    const errs: ManualErr = {};
    const cleanName = sanitizeName(manual.name || "");
    const amazonId = extractASIN(manual.amazonId || "");

    if (!cleanName) errs.name = "Product name is required.";
    if (!amazonId) errs.amazonId = "Enter a valid ASIN or Amazon product URL.";
    if (amazonId && knownAsins.has(amazonId))
      errs.amazonId = "Duplicate ASIN; already added.";

    setManualErr(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreateProduct = () => {
    if (!validateManual()) return;
    const amazonId = extractASIN(manual.amazonId!)!;
    const next: UploadProduct = {
      ...manual,
      name: sanitizeName(manual.name!),
      amazonId,
    };
    setProducts((prev) => [...prev, next]);
    setManual({
      name: "",
      sku: "",
      amazonId: "",
      upc: "",
      brand: "",
      map: "",
    });
  };

  const handleSaveAndContinue = async () => {
    const updatedConfig = {
      props: {
        onboarding: {
          currentStep: ONBOARDING_STEPS.verifyEmail,
          productCatalog: products,
        },
      },
    };

    await updateUserConfig(auth, globalConfig, updatedConfig);

    try {
      await sendAlert({
        customerName: globalConfig?.user?.user,
        step: ONBOARDING_STEPS.catalog,
        catalog: products,
      });
    } catch (emailError) {
      console.error("Failed to send alert email:", emailError);
    }

    navigate(`${ENROLL_PATH}/${ONBOARDING_STEPS.verifyEmail}`);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Stepper currentStep={2} />
      <Box
        sx={{
          flex: 1,
          padding: { xs: 3, md: "0px 48px" },
          backgroundColor: "#ffffff",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Card
          sx={{
            maxWidth: 1200,
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: "#343a40",
                mb: 1,
                fontSize: { xs: "24px", md: "32px" },
              }}
            >
              Upload Your Product Catalog
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#6c757d",
                mb: 2,
                lineHeight: 1.6,
                maxWidth: "600px",
              }}
            >
              Tell us which products you'd like to monitor. We recommend
              starting with your top-selling items. You can always add more
              later!
            </Typography>

            <Alert severity="info" sx={{ mb: 3, maxWidth: "800px" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Free Trial Limits
              </Typography>
              <Typography variant="body2">
                Our free trial is currently limited to{" "}
                <strong>Amazon products only</strong> with a maximum of{" "}
                <strong>1,000 ASINs</strong>.
              </Typography>
            </Alert>

            <Box
              sx={{
                mb: 4,
                p: 2,
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                maxWidth: "800px",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#343a40", mb: 1.5 }}
              >
                Two Ways to Add Products:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <li style={{ marginBottom: "8px" }}>
                  <strong>Bulk Upload:</strong> Import your entire catalog via
                  CSV or Excel file
                </li>
                <li>
                  <strong>Manual Entry:</strong> Add products individually to
                  start monitoring right away
                </li>
              </Box>
            </Box>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#343a40", mb: 2 }}
              >
                Upload Product Catalog
              </Typography>

              <Button
                variant="contained"
                onClick={() => setModalOpen(true)}
                startIcon={<UploadFile />}
                sx={{
                  backgroundColor: "#343a40",
                  color: "white",
                  px: 3,
                  py: 1.5,
                  fontSize: "14px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  "&:hover": { backgroundColor: "#23272b" },
                }}
              >
                Add Products
              </Button>
            </Box>
            <ManualUpload
              manual={manual}
              manualErr={manualErr}
              onManualChange={handleManualChange}
              onCreateProduct={handleCreateProduct}
            />
            {products.length > 0 && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {products.length} product{products.length > 1 ? "s" : ""}{" "}
                  added
                </Alert>
                <ProductPreview
                  onDelete={(index) =>
                    setProducts((prev) =>
                      prev.filter((_, idx) => idx !== index)
                    )
                  }
                  onUpdate={(index, updatedProduct) => {
                    setProducts((prev) =>
                      prev.map((p, idx) => (idx === index ? updatedProduct : p))
                    );
                  }}
                  products={products}
                />
              </>
            )}

            <SaveAndContinueButton
              onConfirm={handleSaveAndContinue}
              disabled={!canContinue}
            />
          </CardContent>
        </Card>

        <ProductUploadModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onAccept={(newProducts) => {
            const existing = new Set(
              products.map((p) => p.amazonId?.toUpperCase()).filter(Boolean)
            );
            const deduped = newProducts.filter(
              (p) => !existing.has((p.amazonId || "").toUpperCase())
            );
            setProducts((prev) => [...prev, ...deduped]);
            setModalOpen(false);
          }}
        />
      </Box>
    </Box>
  );
};

export default ProductCatalogUpload;
